// Onboarding clarity / juice (POWERUP-06) — power-up ACTIVATE overlay (§10.3).
//
// Six of the eight power-ups previously committed with audio only and NO
// on-screen confirmation. This brief, theme-tinted overlay fires the moment a
// power-up is COMMITTED (not at tray-select — see the INT-07 fix) and surfaces
// the power-up's canon line icon + name + a one-line "what just happened"
// subtitle, then auto-dismisses (~dur-cinematic-short). Structurally mirrors
// QueenRises (the §10.x signature-moment pattern) so the four themes + reduced
// motion are handled identically.
//
// Reduced motion (UI-SPEC §5 / §8): the auto-dismiss collapses to dur-quick
// (200ms) via selectDuration, the magic-bloom sparks are suppressed, and the
// scrim renders at a softer fixed opacity with no entrance transform — the
// fully-formed overlay simply appears and clears. Audio (the 'powerup' cue) is
// owned by the caller and is NOT gated by reduced motion (audio policy).

import { useStore } from '@nanostores/react'
import { type CSSProperties, type JSX, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { selectDuration } from '../../game/motion/tween-config'
import type { PowerUpName } from '../../game/PlayerViewSchema'
import { $reducedMotion } from '../../stores/motion'
import { PowerIcon } from '../components/PowerIcon'
import { useAnnounce } from '../components/useAnnounce'
import { POWERUP_ICON } from './PowerupTray'

const SPARK_COUNT = 12

export interface PowerupActivateOverlayProps {
  readonly name: PowerUpName
  readonly onComplete: () => void
}

export function PowerupActivateOverlay({
  name,
  onComplete,
}: PowerupActivateOverlayProps): JSX.Element {
  const reducedMotion = useStore($reducedMotion)
  const intl = useIntl()
  const announce = useAnnounce()

  // §10.3 — a brief moment; dur-cinematic-short (800ms) collapses to dur-quick
  // (200ms) under reduced motion via the canonical substitution.
  const durationMs = selectDuration('dur-cinematic-short', reducedMotion)
  useEffect(() => {
    const id = window.setTimeout(onComplete, durationMs)
    return () => {
      window.clearTimeout(id)
    }
  }, [onComplete, durationMs])

  // SR confirmation — polite, once on mount.
  useEffect(() => {
    announce(
      intl.formatMessage({ id: 'a11y.announce.powerup_activated' }),
      'polite',
    )
  }, [announce, intl])

  const nameLabel = intl.formatMessage({ id: `match.powerup.name.${name}` })

  return (
    <div
      role="presentation"
      data-screen-region="powerup-activate"
      data-powerup-activate={name}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        // §10.3 theme-magic bloom scrim — color-mix keeps it theme-aware across
        // all four palettes (THEME-04). Softer under reduced motion.
        background: `radial-gradient(circle at 50% 50%, color-mix(in srgb, var(--color-magic) ${
          reducedMotion ? 35 : 55
        }%, transparent) 0%, transparent 60%)`,
      }}
    >
      {!reducedMotion
        ? Array.from({ length: SPARK_COUNT }).map((_, i) => {
            const angle = (i / SPARK_COUNT) * Math.PI * 2
            const r = 64
            const dx = Math.cos(angle) * r
            const dy = Math.sin(angle) * r
            const sparkStyle: CSSProperties = {
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 6,
              height: 6,
              borderRadius: 'var(--radius-pill)',
              background: 'var(--color-magic)',
              boxShadow: '0 0 12px var(--color-magic)',
              // Reuse the queenSpark keyframe (owns transform); offset via props.
              ['--dx' as string]: `${dx}px`,
              ['--dy' as string]: `${dy}px`,
              transform: 'translate(var(--dx), var(--dy))',
              opacity: 0.85,
              animation: 'queenSpark 800ms ease-out',
              animationDelay: `${i * 30}ms`,
            }
            return (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: 12 fixed radial positions, never reordered
                key={i}
                aria-hidden="true"
                style={sparkStyle}
              />
            )
          })
        : null}

      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-mid)',
          border: '2px solid var(--color-surface-stroke)',
          color: 'var(--color-cta-primary)',
        }}
      >
        <PowerIcon name={POWERUP_ICON[name]} size={32} title={nameLabel} />
      </span>

      <h1
        className="display-word gradient"
        style={{
          textAlign: 'center',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-22)',
          margin: 0,
        }}
      >
        {nameLabel}
      </h1>
      <span
        style={{
          textAlign: 'center',
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-secondary)',
          maxWidth: 'calc(var(--space-6) * 9)',
        }}
      >
        <FormattedMessage id={`powerup.activate.${name}.subtitle`} />
      </span>
    </div>
  )
}
